import { HttpClient } from '@angular/common/http';
import { inject, InjectionToken, Provider } from '@angular/core';
import { LaravelResult } from '@fsf/util';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  BevHoldersQuery,
  BevHoldersResult,
  bevHoldersResultSchema,
} from '../models/site-bev-holders.model';
import { SiteBevHoldersRepository } from './site-bev-holders.repository';

/** Behind a token so the `@fsf/discover` lib takes no dependency on the app's `environment`. */
export const SITE_BEV_HOLDERS_ENDPOINT = new InjectionToken<string>(
  'site.bev-holders.endpoint.url',
);

export interface SiteBevHoldersApiConfig {
  http: HttpClient;
  endpointUrl: string;
}

/**
 * The server does the three-level match; this repository unwraps and Zod-parses before
 * emitting, so contract drift errors the stream rather than emitting a malformed result.
 *
 * Not `@Injectable` — only ever built via `new`, so AOT must not analyze its config-object
 * constructor parameter as a DI token.
 */
export class SiteBevHoldersApiRepository extends SiteBevHoldersRepository {
  readonly #http: HttpClient;
  readonly #endpointUrl: string;

  constructor(config: SiteBevHoldersApiConfig) {
    super();
    this.#http = config.http;
    this.#endpointUrl = config.endpointUrl;
  }

  getBevHolders(query: BevHoldersQuery): Observable<BevHoldersResult> {
    return this.#http
      .get<LaravelResult<unknown>>(this.#endpointUrl, {
        params: {
          country: query.country,
          // The state slot keys on the site's ISO 3166-2 code, not the region name.
          state: query.state,
          postcode: query.postcode,
        },
      })
      .pipe(map((result) => bevHoldersResultSchema.parse(result.data)));
  }
}

/** `inject` runs inside the factory, which is an injection context. */
export function provideSiteBevHoldersApi(): Provider {
  return {
    provide: SiteBevHoldersRepository,
    useFactory: () =>
      new SiteBevHoldersApiRepository({
        http: inject(HttpClient),
        endpointUrl: inject(SITE_BEV_HOLDERS_ENDPOINT),
      }),
  };
}
